import { useEffect, useRef, useSyncExternalStore } from 'react'
import { isStale, onStale } from '../api.js'
import { useText } from '../i18n.js'

/**
 * Hinweis, dass dieser Tab mit veraltetem JavaScript läuft.
 *
 * Bewusst ein Balken oben und kein Toast unten: Ein Toast verschwindet aus dem
 * Blick und liegt auf dem Board dort, wo ohnehin schon Meldungen erscheinen.
 * Hier geht es aber nicht um den letzten Handgriff, sondern darum, dass ab
 * jetzt jeder Handgriff schiefgehen kann — das darf stehen bleiben, bis jemand
 * neu lädt.
 *
 * Kein automatisches Neuladen: Auf einem Board kann ungesicherte Arbeit liegen,
 * und ein Vortrag, der sich mitten im Satz neu lädt, wäre schlimmer als der
 * Fehler, den es zu verhindern gilt.
 */
export function StaleVersionNotice(): React.ReactElement | null {
  const stale = useSyncExternalStore(onStale, isStale)
  const t = useText()
  const ref = useRef<HTMLDivElement>(null)

  /*
   * Der Balken liegt fest am oberen Rand und würde sonst die Kopfleiste
   * verdecken — auf der Übersicht den Organisationswechsel, auf dem Board
   * Titel, Freigabe und Präsentation. Deshalb bekommt der Körper genau die
   * Höhe des Balkens als Innenabstand.
   *
   * Gemessen statt geschätzt: Der Satz bricht auf schmalen Fenstern um, eine
   * feste Zahl im CSS wäre dort zu klein und verdeckte wieder etwas.
   */
  useEffect(() => {
    const node = ref.current
    if (!stale || !node) return
    const apply = (): void => {
      document.body.style.paddingTop = `${node.offsetHeight}px`
    }
    apply()
    const observer = new ResizeObserver(apply)
    observer.observe(node)
    return () => {
      observer.disconnect()
      document.body.style.paddingTop = ''
    }
  }, [stale])

  if (!stale) return null
  return (
    <div className="stale-banner" role="alert" ref={ref}>
      <span>{t('update.notice')}</span>
      <button className="primary" onClick={() => window.location.reload()}>
        {t('update.reload')}
      </button>
    </div>
  )
}
